import { useFormik } from 'formik'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { Avatar } from '../../components/avatar'
import { VendorImage } from '../../components/avatar/vendorImage'
import { Button } from '../../components/button'
import { Checkbox } from '../../components/inputs/Checkbox'
import { SearchInput } from '../../components/inputs/searchInput'
import { ChooseProductOptionsModal } from '../../components/modal/app_modals/chooseProductOptionsModal'
import { POSViewOpenOrdersModal } from '../../components/modal/app_modals/POSViewOpenOrdesModal'
import { SelectCreatorModal } from '../../components/modal/app_modals/selectCreatorModal'
import { TabNav } from '../../components/nav/tabNav'
import { TableComponent } from '../../components/table'
import { PageHeader } from '../../components/typography/PageHeader'
import {
  calculateProductPrice,
  getPageFromSkip,
  getSkipFromPage,
} from '../../helpers'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { useModalControl } from '../../hooks/useModalControl'
import Layout from '../../layout'
import {
  updateAdminCartOrder,
  updateCurrentCartId,
  updatePOSCreator,
} from '../../redux-toolkits/auth/auth.reducer'
import { useFetchListCategoriesQuery } from '../../redux-toolkits/lists/list.slice'
import {
  useAddItemToCartMutation,
  useRemoveItemFromCartMutation,
} from '../../redux-toolkits/orders/orders.slice'
import { FulfilmentType } from '../../redux-toolkits/orders/orders.type'
import {
  AddItemToCart,
  CheckoutProvider,
  PaymentMethods,
} from '../../redux-toolkits/cart/cart.type'
import { useGetAllProductsQuery } from '../../redux-toolkits/products/products.slice'
import { ProductSearchQuery } from '../../redux-toolkits/products/products.type'
import {
  SingleTerminal,
  TerminalProvider,
} from '../../redux-toolkits/terminals/terminals.types'
import { FulfilmentCentre } from '../../redux-toolkits/fulfilment-centres/fulfilmentCentres.type'
import { Product } from '../../redux-toolkits/products/products.type'
import { POSSchema } from '../../validation/validate'
import { CartSummary } from './cartSummary'

export type POSOrderDetails = {
  customerName: string
  customerEmail: string
  customerPhone: string
  customerId: string
  paymentMethod: PaymentMethods
  fulfilmentType: FulfilmentType
  orderNote: string
  fulfilmentCenter?: FulfilmentCentre
  provider: CheckoutProvider
  terminalProvider: TerminalProvider
  terminal?: SingleTerminal
  tableNumber?: string
  isOpenOrder: boolean
  createGoodAccount?: boolean
}

export const POSPage = () => {
  const {
    currentCart,
    adminCartOrder,
    adminCartItems,
    POSFulfilmentCenter,
    selectedPOSTerminal,
    POSCreator,
  } = useAppSelector((state) => state.auth)

  const dispatch = useAppDispatch()

  const { handleModalClose, handleModalOpen, setModalOpen, modal, modalOpen } =
    useModalControl()

  const [searchQuery, setSearchQuery] = useState<ProductSearchQuery>({
    skip: 0,
    limit: 20,
    search: undefined,
    categoryId: undefined,
  })
  const [selectedProduct, setSelectedProduct] = useState<Product>()

  const formikVar = useFormik({
    initialValues: {
      customerEmail: adminCartOrder?.personalInformation.email ?? '',
      customerName: '',
      customerPhone: '',
      customerId: '',
      fulfilmentType: 'PICKUP',
      paymentMethod: 'bank_transfer',
      provider: 'MONNIFY',
      orderNote: '',
      fulfilmentCenter: POSFulfilmentCenter,
      terminalProvider: selectedPOSTerminal?.provider ?? 'PAYSTACK',
      terminal: selectedPOSTerminal,
      tableNumber: '',
    } as POSOrderDetails,
    enableReinitialize: false,
    validateOnChange: true,
    validationSchema: POSSchema,
    onSubmit: () => {},
  })

  const {
    data: products,
    isFetching: fetchingProducts,
    refetch: refetchProducts,
  } = useGetAllProductsQuery(
    {
      ...searchQuery,
      search: searchQuery.search || undefined,
      status: 'active',
      inStock: true,
      fulfillmentCenterId: POSFulfilmentCenter?._id,
      vendorId: POSCreator?._id,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  )

  const { data: categories, isLoading: loadingCategories } =
    useFetchListCategoriesQuery(
      {
        skip: 0,
        limit: 1000,
        search: undefined,
        isAdmin: false,
      },
      {
        refetchOnMountOrArgChange: true,
      },
    )

  const [addItemToCart, { isLoading: addingItemToCart }] =
    useAddItemToCartMutation()

  const [removeItemFromCart, { isLoading: removingItemFromCart }] =
    useRemoveItemFromCartMutation()

  const prev = useRef(POSFulfilmentCenter)
  useEffect(() => {
    // Only reset cart when fulfilment center has been changed
    if (prev.current?._id !== POSFulfilmentCenter?._id) {
      formikVar.setFieldValue('fulfilmentCenter', POSFulfilmentCenter)

      dispatch(updateCurrentCartId(undefined))
      dispatch(updateAdminCartOrder(undefined))
    }
  }, [POSFulfilmentCenter])

  useEffect(() => {
    formikVar.setValues({
      ...formikVar.values,
      terminal: selectedPOSTerminal,
      terminalProvider: selectedPOSTerminal?.provider ?? 'PAYSTACK',
    })

    formikVar.validateField('terminal')
  }, [selectedPOSTerminal])

  useEffect(() => {
    formikVar.setFieldValue(
      'customerEmail',
      adminCartOrder?.personalInformation.email ?? '',
    )
  }, [adminCartOrder?.personalInformation.email])

  return (
    <Layout>
      <div className="flex flex-col gap-10">
        <div className="flex flex-wrap items-center gap-4 justify-between">
          <PageHeader title={`POS Retail`} className="!mb-0" />

          <Button
            type="button"
            label={`${POSCreator?.name ?? 'All'}`}
            onClick={() => {
              handleModalOpen('selectCreatorModalModal')
            }}
            icon="caret-down"
            iconPosition="right"
            primary={false}
            className="ml-auto"
            labelClassName="max-w-[150px] whitespace-nowrap !text-ellipsis !line-clamp-1"
          />
        </div>

        <div className="flex items-center justify-between gap-4">
          <p className="font-recoleta font-medium text-lg">
            {adminCartOrder?.harmonizedReference
              ? `Order #${adminCartOrder?.harmonizedReference}`
              : ''}
          </p>

          <Button
            type="button"
            label={`See open orders`}
            onClick={() => {
              handleModalOpen('POSViewOpenOrdersModal')
            }}
            icon="side-menu"
            iconPosition="right"
            primary={false}
            variant="plain"
            className="ml-auto !px-0 !py-0"
            labelClassName="max-w-[150px] whitespace-nowrap !text-ellipsis !line-clamp-1"
          />
        </div>

        <div className="flex flex-col lg:flex-row items-start gap-10">
          {/* Products  */}
          <div className="w-full lg:max-w-[600px] flex flex-col gap-6">
            <SearchInput
              searchFunction={(searchText) => {
                setSearchQuery({
                  ...searchQuery,
                  skip: 0,
                  search: searchText,
                })
              }}
              className="!max-w-full"
            />

            <TabNav
              activeTab={searchQuery.categoryId ?? 'all'}
              setActiveTab={(val) => {
                setSearchQuery({
                  ...searchQuery,
                  skip: 0,
                  categoryId: val === 'all' ? undefined : val,
                })
              }}
              tabs={[
                {
                  name: 'All',
                  id: 'all',
                },
                ...(categories?.data?.data ?? []).map((cat) => {
                  return {
                    name: cat.name,
                    id: cat._id,
                  }
                }),
              ]}
              loading={loadingCategories}
            />

            <div className="overflow-x-scroll">
              <TableComponent
                headers={['', 'PRODUCT', 'AMOUNT', 'QUANTITY', 'CREATOR']}
                rows={(products?.data?.data ?? []).map((p) => {
                  return {
                    id: p._id,
                    content: [
                      <Checkbox
                        checkStatus={
                          adminCartItems
                            ?.map((cartItem) => cartItem.product._id)
                            .includes(p._id) ?? false
                        }
                        handleChange={() => {}}
                        key={`${p._id}-checkbox`}
                        disabled
                      />,
                      p.name,
                      `${p.isCombo ? 'From: ' : ''}${calculateProductPrice(p)}`,
                      p.inStockQuantity ?? 'N/A',
                      <div
                        key={`${p._id}-creator`}
                        className="flex gap-2 items-center"
                      >
                        {p.vendor.logo ? (
                          <VendorImage img={p?.vendor?.logo} />
                        ) : (
                          <Avatar
                            name={`${p?.vendor?.businessInformation?.businessName}`}
                            useRandomBgColor={false}
                          />
                        )}
                        <span className="">
                          {p?.vendor?.businessInformation?.businessName}
                        </span>
                      </div>,
                    ],
                  }
                })}
                isRowClickable
                onRowClick={(id) => {
                  if (addingItemToCart) {
                    return
                  }

                  const productsInCart =
                    adminCartItems?.map((cartItem) => cartItem.product._id) ??
                    []

                  if (products?.data?.data) {
                    const clickedProduct = products.data.data.find(
                      (pr) => pr._id === id,
                    )

                    if ((clickedProduct?.inStockQuantity ?? 0) <= 0) {
                      toast.error('Item out of stock')
                      return
                    }

                    if (clickedProduct) {
                      // For combo and variant products
                      if (clickedProduct.isCombo || clickedProduct?.isVariant) {
                        setSelectedProduct(clickedProduct)
                        handleModalOpen('chooseProductOptionsModal')

                        return
                      }
                      // For normal products
                      else {
                        if (productsInCart.includes(clickedProduct._id)) {
                          return // If product already in cart, do nothing
                        } else {
                          addItemToCart({
                            id: currentCart!,
                            fulfillmentcenterId: POSFulfilmentCenter?._id,
                            payload: {
                              productId: clickedProduct._id,
                              quantity: 1,
                            },
                          })
                          return
                        }
                      }
                    }
                  }
                }}
                loading={fetchingProducts}
                isEmpty={[0, undefined].includes(products?.data?.data?.length)}
                currentPage={getPageFromSkip(
                  searchQuery.skip!,
                  searchQuery.limit!,
                )}
                totalDataCount={products?.data?.totalItems ?? 0}
                pageLimit={Number(searchQuery?.limit ?? 0)}
                onPageChange={(page) => {
                  setSearchQuery({
                    ...searchQuery,
                    skip: getSkipFromPage(page, searchQuery.limit!),
                  })
                }}
              />
            </div>
          </div>

          {/* Cart Summary  */}
          <CartSummary
            className="w-full lg:w-fit flex-1 h-fit sticky top-6"
            addingItemToCart={addingItemToCart}
            formikVar={formikVar}
            refetchProducts={() => {
              refetchProducts()
            }}
          />
        </div>
      </div>

      {selectedProduct && (
        <ChooseProductOptionsModal
          open={modal === 'chooseProductOptionsModal' && modalOpen}
          setOpen={setModalOpen}
          onCloseCallback={() => {
            setSelectedProduct(undefined)
            handleModalClose()
          }}
          onSuccessCallback={async (product, comboSel, variantComboSel) => {
            const newComboSelections = comboSel
              ?.map((parent) => {
                return parent.items.map((it) => ({
                  id: it.combo._id,
                  quantity: it.quantity,
                }))
              })
              ?.flat()
              ?.filter((i) => i.quantity > 0)

            const newVariantSelection =
              variantComboSel?.variantCombination?.map((i) => ({
                value: i.value,
                price:
                  product.variants.find(
                    (v) =>
                      v.variant._id === i.variant._id && v.value === i.value,
                  )?.price ?? 0,
                variantId: i.variant._id,
              }))

            const cartEntry = adminCartItems?.find(
              (i) => i.product._id === product._id,
            )

            const newCartItem: AddItemToCart = {
              productId: product._id,
              quantity: 1,
              comboItems: newComboSelections,
              variants: newVariantSelection,
            }

            await addItemToCart({
              id: currentCart!,
              fulfillmentcenterId: POSFulfilmentCenter?._id,
              payload: newCartItem,
            })

            setSelectedProduct(undefined)
            handleModalClose()
          }}
          product={selectedProduct}
          loading={removingItemFromCart || addingItemToCart}
        />
      )}

      <SelectCreatorModal
        open={modalOpen && modal === 'selectCreatorModalModal'}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
        }}
        onSuccessCallback={(creators) => {
          dispatch(
            updatePOSCreator(creators.length > 0 ? creators[0] : undefined),
          )
          handleModalClose()
        }}
        selectedCreators={POSCreator ? [POSCreator] : undefined}
        maxSelection={1}
        loading={false}
        fulfillmentCenters={
          POSFulfilmentCenter ? [POSFulfilmentCenter] : undefined
        }
      />

      {POSFulfilmentCenter &&
      modalOpen &&
      modal === 'POSViewOpenOrdersModal' ? (
        <POSViewOpenOrdersModal
          modalOpen={modalOpen && modal === 'POSViewOpenOrdersModal'}
          handleModalClose={() => {
            handleModalClose()
          }}
          setModalOpen={setModalOpen}
          onSucess={(orders, cart) => {
            dispatch(updateCurrentCartId(cart._id))
            dispatch(updateAdminCartOrder(orders[0]))
          }}
          fulfilmentCentre={POSFulfilmentCenter}
        />
      ) : undefined}
    </Layout>
  )
}
